export enum MappingState {
  /**
   * A server-side suggestion is pre-filled in the combobox but has NOT been
   * applied yet. It only becomes a real, persisted assignment once the user
   * confirms it (which turns the row into {@link MappingState.ManuallyMapped}).
   *
   * Suggestions are deliberately never applied on the user's behalf: the backend
   * derives them from a name match and two entities can legitimately carry the
   * same name (a class and its grade do in the upper school), so a blind bulk
   * apply produced requests that assign one Azure group twice (Bug #11517).
   */
  AutoMapped = 'AutoMapped',
  /**
   * The lesson is explicitly mapped to an Azure group. Only rows in this state
   * are emitted on save.
   */
  ManuallyMapped = 'ManuallyMapped',
  /**
   * No Azure group is applied. The row may still carry a (not-yet-applied)
   * suggestion.
   */
  Unassigned = 'Unassigned',
}

export const getSequencePositionOfMappingState = (
  mappingState: MappingState,
): number =>
  [
    MappingState.Unassigned,
    MappingState.AutoMapped,
    MappingState.ManuallyMapped,
  ].indexOf(mappingState);

export enum ErrorType {
  none = 0,
  cloudCourseError,
  /**
   * The row's Azure group is also assigned to another row. Only raised while the
   * organization has `allowMultipleAzureGroupUsage` disabled, in which case the
   * backend rejects such a request outright.
   */
  duplicateAzureGroup,
}

export const getSequencePositionOfErrorType = (
  error: ErrorType | undefined,
): number => {
  const sequence = [
    ErrorType.cloudCourseError,
    ErrorType.duplicateAzureGroup,
    ErrorType.none,
    undefined,
  ];
  const errorIndex = sequence.indexOf(error);
  return errorIndex < 0 ? sequence.indexOf(ErrorType.none) : errorIndex;
};

export enum CourseExport {
  timetableCourses = 0,
  mnsProCourse,
  all,
  assignment,
}
